"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import {
  FiArrowRight,
  FiClock,
  FiFacebook,
  FiInstagram,
  FiMail,
  FiMapPin,
  FiPhone,
  FiTwitter,
  FiYoutube,
} from "react-icons/fi";
import {
  GiCoffeeMug,
  GiCookingPot,
  GiCupcake,
  GiKnifeFork,
  GiToaster,
} from "react-icons/gi";
import { Link } from "react-router-dom";

const Footer = () => {
  const [activeSection, setActiveSection] = useState(null);

  const footerSections = [
    {
      title: "Shop",
      icon: GiCookingPot,
      items: ["Cookware", "Bakeware", "Small Appliances", "Cutlery", "Dining"],
    },
    {
      title: "Learn",
      icon: GiKnifeFork,
      items: ["Recipes", "Cooking Tips", "Product Guides", "Blog", "Videos"],
    },
    {
      title: "Support",
      icon: GiToaster,
      items: ["FAQs", "Shipping", "Returns", "Warranty", "Contact Us"],
    },
    {
      title: "About",
      icon: GiCoffeeMug,
      items: ["Our Story", "Careers", "Press", "Sustainability", "Partners"],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <footer className="py-16 mt-12 text-white bg-gray-900">
      <div className="container px-4 mx-auto">
        {/* Interactive Footer Sections */}
        <motion.div
          className="grid grid-cols-1 gap-8 mb-12 md:grid-cols-2 lg:grid-cols-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {footerSections.map((section, index) => (
            <motion.div
              key={index}
              className="relative"
              variants={itemVariants}
              onMouseEnter={() => setActiveSection(index)}
              onMouseLeave={() => setActiveSection(null)}
            >
              <div className="flex items-center mb-4">
                <section.icon className="mr-2 text-2xl text-amber-500" />
                <h3 className="text-xl font-bold">{section.title}</h3>
              </div>
              <AnimatePresence>
                {activeSection === index && (
                  <motion.ul
                    className="space-y-2"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {section.items.map((item, itemIndex) => (
                      <motion.li
                        key={itemIndex}
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: itemIndex * 0.1 }}
                      >
                        <Link
                          to="#"
                          className="flex items-center text-gray-300 transition-colors hover:text-amber-500"
                        >
                          <FiArrowRight className="mr-2" />
                          {item}
                        </Link>
                      </motion.li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
              {activeSection !== index && (
                <p className="text-sm text-gray-400">
                  Hover to explore {section.title.toLowerCase()} options
                </p>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Newsletter and Social Media */}
        <motion.div
          className="flex flex-col items-center justify-between py-8 border-t border-gray-700 md:flex-row"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
        >
          <motion.div className="mb-6 md:mb-0" variants={itemVariants}>
            <h3 className="mb-2 text-xl font-bold">Stay Connected</h3>
            <form className="flex">
              <input
                type="email"
                placeholder="Your email"
                className="px-4 py-2 text-white bg-gray-800 rounded-l-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <motion.button
                type="submit"
                className="px-4 py-2 text-white bg-amber-500 rounded-r-md"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </form>
          </motion.div>
          <motion.div className="flex space-x-4" variants={itemVariants}>
            {[FiInstagram, FiFacebook, FiTwitter, FiYoutube].map(
              (Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  className="text-gray-400 hover:text-amber-500"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                >
                  <Icon size={24} />
                </motion.a>
              )
            )}
          </motion.div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          className="grid grid-cols-1 gap-4 py-8 border-t border-gray-700 md:grid-cols-2 lg:grid-cols-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
        >
          {[
            { icon: FiMail, text: "support@tokhamienterprises.com" },
            { icon: FiPhone, text: "+1 (555) 123-4567" },
            {
              icon: FiMapPin,
              text: "Tokha -3 , Kathmandu, Nepal",
            },
            { icon: FiClock, text: "Sun-Fri: 9AM-6PM EST" },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="flex items-center space-x-3"
              variants={itemVariants}
            >
              <item.icon className="text-amber-500" size={20} />
              <span className="text-sm text-gray-300">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Logo and Copyright */}
        <motion.div
          className="flex flex-col items-center justify-between pt-8 border-t border-gray-700 md:flex-row"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
        >
          <motion.div variants={itemVariants} className="mb-4 md:mb-0">
            <Link to="/" className="flex items-center">
            <img
          src="/logo.png" // Path to the logo in the public folder
          alt="Tokhami Enterprises Pvt. Ltd. Logo"
          className="w-12 h-12 mr-2" // Adjust width and height as needed
        />
              <span className="text-2xl font-bold">Tokhami Enterprises Pvt. Ltd.</span>
            </Link>
          </motion.div>
          <motion.p variants={itemVariants} className="text-sm text-gray-400">
            &copy; 2025 Tokhami Enterprises Pvt. Ltd.. All rights reserved. Crafted with passion
            for culinary enthusiasts.
          </motion.p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
